import { useState, useEffect } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { cn } from "@/lib/utils";

export default function LoginPage() {
	const { login, isAuthenticated } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		const message = (location.state as { toast?: string } | null)?.toast;
		if (message) toast.success(message);
	}, []);

	useEffect(() => {
		if (isAuthenticated) navigate("/");
	}, [isAuthenticated]);

	const [showPassword, setShowPassword] = useState(false);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	if (isAuthenticated) return <Navigate to="/" replace />;

	function handleGoogleSignIn() {
		const apiUrl =
			import.meta.env.VITE_API_URL ?? "http://localhost:5050/api/v1";
		window.location.href = `${apiUrl}/auth/google`;
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError("");
		if (!email || !password) {
			setError("Please fill in all fields.");
			return;
		}
		setLoading(true);
		try {
			await login(email, password, true);
			navigate("/");
		} catch (err) {
			setError(err instanceof Error ? err.message : "Login failed.");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="flex min-h-svh bg-background lg:min-h-screen">
			{/* Left panel */}
			<div className="hidden w-[420px] shrink-0 flex-col justify-between bg-primary/90 dark:bg-blue-900/10 p-8 xl:flex xl:w-[480px] xl:p-10">
				<Link
					to="/"
					aria-label="TaskHub home"
					className="flex items-center"
				>
					<img
						src="/favicon.svg"
						alt="TaskHub"
						className="h-10 w-10"
					/>
					<span className="ml-2 text-primary-foreground dark:text-blue-300 text-lg font-bold tracking-tight">
						TaskHub
					</span>
				</Link>

				<div>
					<blockquote className="text-primary-foreground dark:text-blue-300/90 text-2xl font-semibold leading-snug tracking-tight mb-4">
						"Focused engineering requires visual quiet and clarity."
					</blockquote>
					<p className="text-primary-foreground dark:text-blue-300/60 text-sm leading-relaxed">
						Manage sprints, track issues, and ship faster all from
						one focused workspace built for engineering teams.
					</p>

					<div className="mt-10 grid grid-cols-3 gap-4">
						{[
							{ label: "Active Teams", value: "240+" },
							{ label: "Tasks Shipped", value: "18k" },
							{ label: "Uptime", value: "99.9%" },
						].map((stat) => (
							<div
								key={stat.label}
								className="rounded-lg bg-primary-foreground/10 dark:bg-primary-foreground px-4 py-3"
							>
								<p className="text-primary-foreground dark:text-blue-300 text-xl font-bold">
									{stat.value}
								</p>
								<p className="text-primary-foreground dark:text-blue-300/60 text-xs mt-0.5">
									{stat.label}
								</p>
							</div>
						))}
					</div>
				</div>

				<p className="text-primary-foreground dark:text-blue-300/30 text-xs">
					© 2026 TaskHub. All rights reserved.
				</p>
			</div>

			{/* Right panel */}
			<div className="flex min-h-svh flex-1 items-start justify-center overflow-y-auto px-4 py-6 sm:items-center sm:px-6 sm:py-10 lg:min-h-screen lg:px-10 xl:px-6 xl:py-12">
				<div className="w-full max-w-[400px]">
					<Link
						to="/"
						aria-label="TaskHub home"
						className="mb-8 flex items-center justify-center xl:hidden"
					>
						<img
							src="/logo.svg"
							alt="TaskHub"
							className="h-8 w-auto max-w-full"
						/>
					</Link>

					<div className="mb-6 sm:mb-8">
						<h1 className="mb-1 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
							Welcome back
						</h1>
						<p className="text-sm text-muted">
							Sign in to your workspace to continue.
						</p>
					</div>

					<form
						onSubmit={handleSubmit}
						noValidate
						className="space-y-4"
					>
						{error && (
							<div className="rounded-lg border border-danger/20 bg-danger-subtle px-4 py-3 text-sm text-danger">
								{error}
							</div>
						)}

						<div>
							<label
								htmlFor="email"
								className="block text-sm font-medium text-muted-foreground mb-1.5"
							>
								Email address
							</label>
							<Input
								id="email"
								type="email"
								placeholder="you@company.com"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								autoComplete="email"
								className={cn(
									error &&
										!email &&
										"border-danger focus-visible:ring-danger",
								)}
							/>
						</div>

						<div>
							<div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
								<label
									htmlFor="password"
									className="text-sm font-medium text-muted-foreground"
								>
									Password
								</label>
								<Link
									to="/forgot-password"
									className="text-xs text-primary hover:underline focus:outline-none focus-visible:underline"
								>
									Forgot password?
								</Link>
							</div>
							<div className="relative">
								<Input
									id="password"
									type={showPassword ? "text" : "password"}
									placeholder="••••••••"
									value={password}
									onChange={(e) =>
										setPassword(e.target.value)
									}
									autoComplete="current-password"
									className={cn(
										"pr-10",
										error &&
											!password &&
											"border-danger focus-visible:ring-danger",
									)}
								/>
								<button
									type="button"
									onClick={() => setShowPassword((v) => !v)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-muted-foreground transition-colors focus:outline-none"
									aria-label={
										showPassword
											? "Hide password"
											: "Show password"
									}
								>
									{showPassword ? (
										<EyeOff className="h-4 w-4" />
									) : (
										<Eye className="h-4 w-4" />
									)}
								</button>
							</div>
						</div>

						<Button
							type="submit"
							className="w-full"
							disabled={loading}
						>
							{loading ? (
								<span className="flex items-center gap-2">
									<span className="h-4 w-4 rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground animate-spin" />
									Signing in…
								</span>
							) : (
								<span className="flex items-center gap-2">
									Sign in <ArrowRight className="h-4 w-4" />
								</span>
							)}
						</Button>
					</form>

					<div className="my-4 flex items-center gap-3">
						<Separator className="flex-1" />
						<span className="shrink-0 text-xs text-muted">
							or sign in with email
						</span>
						<Separator className="flex-1" />
					</div>

					<Button
						variant="outline"
						className="w-full mb-4 gap-2"
						type="button"
						onClick={handleGoogleSignIn}
					>
						<GoogleIcon className="h-4 w-4" />
						Continue with Google
					</Button>

					<p className="mt-6 text-center text-sm text-muted">
						Don't have an account?{" "}
						<Link
							to="/register"
							className="text-primary font-medium hover:underline"
						>
							Request access
						</Link>
					</p>
				</div>
			</div>
		</div>
	);
}
